import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { ValidationError } from 'yup'
import { useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import {
  ButtonV2,
  ModalConfirmV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtonsV2,
  TextInputV2,
  ModalSizeVariants,
  SelectV2,
  ProductListingSelector,
} from '@labrav/react-components'

import { Product } from '@labrav/react-components/lib/@types/types/forms'
import { SelectChangeEvent } from '@mui/material'
import { useSelector } from 'react-redux'
import {
  useAccountId,
  useCurrentCloudProvider,
  isLoading,
  LoadingTypes,
} from '@labrav/common'
import type { ComponentLoadingState } from '@labrav/common'

import { useStyles } from './AddProductModal.styles'
import {
  FlyOutAWSProductListingType,
  ProductStatus,
} from '../../../modules/productsListing/types'
import aws from '../../images/aws.png'
import { awsContactUsURL } from '../../utils/constants'
import { websiteValidationCheckMessage } from '../../utils/messagesConstants'
import {
  externalMarketplaceJson,
  mapProducts,
} from '../../utils/productCreationUtil'
import { FlyoutListingProductType } from '../../../types/addEditProduct'
import { getProductIDForOtherProduct } from '../../../modules/addEditProduct/action'

const AWSProductType: RadioButtonGroupMapping[] = [
  {
    key: FlyOutAWSProductListingType.SAAS,
    label: 'Software-as-a-Service (SaaS)',
    type: RadioButtonType.STANDARD,
    disabled: false,
    description:
      'You deploy software hosted on AWS infrastructure and grant buyers access to the software in your AWS environment. You have to manage customer access, account creation, resource provisioning, and account management within your software.',
  },
  {
    key: FlyOutAWSProductListingType.AGENTIC_AI,
    label: 'Agentic AI',
    type: RadioButtonType.STANDARD,
    disabled: false,
    description:
      'AI agents that customers can automatically deploy using MCP or A2A protocols. These agents run on AWS infrastructure and are managed as an extension of your existing SaaS solution.',
  },
  {
    key: FlyOutAWSProductListingType.PROFESSIONAL_SERVICES,
    label: 'Professional service',
    type: RadioButtonType.STANDARD,
    disabled: false,
    description:
      'Professional services include services to assess, migrate, support, manage, and train others in how to use AWS services and products in AWS Marketplace. Sellers create a product offering that describes the services they provide, negotiate with customers to create an agreement on terms, and then create a custom offer for services through AWS Marketplace.',
  },
  {
    key: FlyOutAWSProductListingType.CONTAINER,
    label: 'Container',
    type: RadioButtonType.CUSTOM,
    disabled: false,
    description:
      'Container products consist of delivery options that are a set of container images and deployment templates that go together. For each delivery option, you provide a set of container images, usage instructions, and links to deployment templates for customers to launch that delivery option.',
  },
]

const validationSchema = yup.object({
  cloud: yup.string().required('Please select an external marketplace'),
  salesforceAppExchangeLink: yup
    .string()
    .url(websiteValidationCheckMessage)
    .when('cloud', {
      is: (cloud: string) => !!cloud,
      then: yup
        .string()
        .required('Please provide a valid Salesforce AppExchange Listing Link'),
      otherwise: yup.string().notRequired(),
    }),
})

export const AddProductModal = ({
  openAddProductModal,
  handleCloseAddProductModal,
}: {
  openAddProductModal: boolean
  handleCloseAddProductModal: () => void
}) => {
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()
  const productListingLoading = useSelector(
    (state: { loading: ComponentLoadingState }) =>
      isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const getToolTioMsg = () => {
    if (productListingLoading) {
      return 'Please wait for some time. Fetching products...'
    } else if (!productRows?.length) {
      return "You don't have product to clone"
    } else if (!mapProducts(productRows)?.length) {
      return 'Cloning is only available for SaaS listings which have been created in the Labra App'
    }
  }
  const listingType: RadioButtonGroupMapping[] = [
    {
      key: FlyoutListingProductType.CLONE,
      label: 'Clone an existing AWS listing',
      type: RadioButtonType.STANDARD,
      disabled: !productRows?.length || !mapProducts(productRows)?.length,
      disabledTooltip: getToolTioMsg(),
    },
    {
      key: FlyoutListingProductType.NEW,
      label: 'Create a brand new AWS listing',
      type: RadioButtonType.STANDARD,
    },
    {
      key: FlyoutListingProductType.OTHER,
      label: 'Fetch listing info from another marketplace',
      type: RadioButtonType.STANDARD,
    },
  ]
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const accountId = useAccountId({ product: 'flyout' })
  const currentAccountId = routeAccountId || accountId || ''
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const [listingTypeInfo, setListingTypeInfo] = useState<string>(
    FlyoutListingProductType.NEW
  )
  const [awsProductType, setAwsProductType] = useState<string>(
    FlyOutAWSProductListingType.SAAS
  )
  const [product, setProduct] = useState<Product | undefined>(undefined)
  const [cloud, setCloud] = useState('')
  const [salesforceAppExchangeLink, setSalesforceAppExchangeLink] = useState('')
  const [touched, setTouched] = useState<{
    cloud: boolean
    salesforceAppExchangeLink: boolean
  }>({ cloud: false, salesforceAppExchangeLink: false })
  const [errors, setErrors] = useState<{
    cloud?: string
    salesforceAppExchangeLink?: string
  }>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resetModal = () => {
    setListingTypeInfo(FlyoutListingProductType.NEW)
    setAwsProductType(FlyOutAWSProductListingType.SAAS)
    setCloud('')
    setSalesforceAppExchangeLink('')
    setTouched({ cloud: false, salesforceAppExchangeLink: false })
    setErrors({})
  }

  const validateFields = async () => {
    setErrorMessage(null)

    try {
      await validationSchema.validate(
        { cloud, salesforceAppExchangeLink },
        { abortEarly: false }
      )
      setErrors({})
      return true
    } catch (error) {
      if (error instanceof ValidationError) {
        const errorObj: { [key: string]: string } = {}
        error.inner.forEach(err => {
          if (err.path) errorObj[err.path] = err.message
        })
        setErrors(errorObj)
      } else {
        console.error(error)
      }
      return false
    }
  }

  useEffect(() => {
    validateFields()
  }, [cloud, salesforceAppExchangeLink])

  const handleBlur = async (field: 'cloud' | 'salesforceAppExchangeLink') => {
    setTouched(prev => ({ ...prev, [field]: true }))
    await validateFields()
  }

  const handleChange = async (event: SelectChangeEvent) => {
    const selectedCloud = event.target.value as string
    setCloud(selectedCloud)
    await validateFields()
  }

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value as string
    setListingTypeInfo(value)
  }

  const handleProductTypeSelection = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value as string
    setAwsProductType(value)
  }

  const handleContactClick = () => {
    window.open(awsContactUsURL, '_blank', 'rel=noopener noreferrer')
  }
  const cloudPath =
    routeCloud?.toLowerCase() || selectedCloudTypeProductListing.toLowerCase()
  const onConfirm = () => {
    if (listingTypeInfo === FlyoutListingProductType.CLONE && product) {
      handleCloseAddProductModal()
      navigate(
        `/flyout/${cloudPath}/${currentAccountId}/products-listing/edit?cloneProductId=${
          product?.productId
        }&cloneProductName=${product?.label}&isDraftListing=${String(
          product.status === ProductStatus.PRODUCT_STATUS_DRAFT
        )}&listingTypeInfo=${FlyoutListingProductType.NEW}&awsProductType=${
          product?.productType
        }`
      )
    } else {
      handleCloseAddProductModal()
      navigate(
        `/flyout/${cloudPath}/${currentAccountId}/products-listing/edit?listingTypeInfo=${listingTypeInfo}&awsProductType=${awsProductType}`
      )
    }
  }

  const onConfirmSFListing = async () => {
    setErrorMessage(null)

    try {
      setTouched({
        cloud: true,
        salesforceAppExchangeLink: !!cloud,
      })

      if (listingTypeInfo === FlyoutListingProductType.OTHER) {
        const isValid = await validateFields()

        if (currentAccountId && isValid) {
          setSubmitLoading(true)
          try {
            const productId = await getProductIDForOtherProduct({
              partnerId: currentAccountId,
              listingLink: salesforceAppExchangeLink,
            })
            setSubmitLoading(false)
            if (productId) {
              sessionStorage.setItem('isSFFirstTime', 'true')
              handleModalClose()
              navigate(
                `/flyout/${cloudPath}/${currentAccountId}/products-listing/${productId}/edit?listingTypeInfo=${FlyoutListingProductType.OTHER}&awsProductType=${awsProductType}`
              )
            } else {
              const errorMsg =
                'Product ID could not be retrieved. Please try again.'
              setErrorMessage(errorMsg)
              console.error(errorMsg)
            }
          } catch (error) {
            const errorMsg =
              'Error retrieving product ID. Please try again later.'
            setErrorMessage(errorMsg)
            setSubmitLoading(false)
            console.error(errorMsg, error)
          }
        }
      } else {
        handleModalClose()
        navigate(
          `/flyout/${cloudPath}/${currentAccountId}/products-listing/edit?listingTypeInfo=${listingTypeInfo}&awsProductType=${awsProductType}`
        )
      }
    } catch (error) {
      const errorMsg = 'An unexpected error occurred. Please try again later.'
      setErrorMessage(errorMsg)
      console.error(errorMsg, error)
    }
  }

  const handleModalClose = () => {
    resetModal()
    handleCloseAddProductModal()
  }

  const disableAccept =
    (listingTypeInfo === FlyoutListingProductType.CLONE && isEmpty(product)) ||
    isEmpty(listingTypeInfo) ||
    isEmpty(awsProductType)

  const showAcceptButton =
    awsProductType !== FlyOutAWSProductListingType.PROFESSIONAL_SERVICES &&
    awsProductType !== FlyOutAWSProductListingType.CONTAINER

  const handleChangeProductListing = async (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const selectedValue = event.target.value as string

    const mappedProducts = mapProducts(productRows)
    const selectedProduct = mappedProducts.find(
      mappedProduct => mappedProduct.value === selectedValue
    )

    setProduct(selectedProduct as Product | undefined)
  }
  return (
    <ModalConfirmV2
      title={'Select an option'}
      scrollContent
      customContentClass={classes.customContent}
      content={
        <div className={classes.root} data-testid={'add-product-selection'}>
          <RadioButtonsV2
            checkedValue={{
              key: listingTypeInfo ?? '',
              value: listingTypeInfo ?? '',
            }}
            name="companyDetails.listingType"
            radioButtonMappings={listingType}
            handleChange={handleSelection}
            gapBetween="16px"
            vertical
          />
          <div>
            {listingTypeInfo === FlyoutListingProductType.NEW && (
              <>
                <div className={classes.managedContent}>
                  <Typography className={classes.description} component="div">
                    You can create a brand new AWS listing from scratch i.e.,
                    forms for Product details, Product dimensions, Buyer
                    registration page, and Notifications will be empty and you
                    can fill them as per your requirement.
                  </Typography>
                  <Typography
                    className={clsx(classes.description, classes.noteWrapper)}
                  >
                    <span className={classes.descriptionNote}>Note:</span> The
                    entire form will be empty and you will be required to fill
                    all mandatory fields to create your listing.
                  </Typography>
                </div>
              </>
            )}
            {listingTypeInfo === FlyoutListingProductType.CLONE ? (
              <>
                <div className={classes.managedContent}>
                  <Typography className={classes.description}>
                    All data from an existing listing of your choice will be
                    copied to the new listing form. The entire listing form
                    i.e., Product details, Product dimensions, Buyer
                    registration page, and Notifications will be copied from the
                    listing you choose.
                  </Typography>
                  <Typography
                    className={clsx(classes.description, classes.noteWrapper)}
                  >
                    <span className={classes.descriptionNote}>Note:</span> You
                    can edit all information after cloning in the next step.
                  </Typography>
                  <div className={classes.productListingSelector}>
                    <ProductListingSelector
                      field={{
                        title: 'Select a listing to clone',
                        id: 'clone',
                        isRequired: false,
                        isReadOnly: false,
                        options: mapProducts(productRows),
                      }}
                      onChange={handleChangeProductListing}
                    />
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
            {listingTypeInfo === FlyoutListingProductType.OTHER && (
              <>
                <div className={classes.managedContent}>
                  <Typography className={classes.description}>
                    All data from an existing listing on another marketplace of
                    your choice will be fetched to the new listing form. Only
                    the common information will be fetched and shown in the
                    form. You will have to fill up all other mandatory fields to
                    be able to create your AWS Marketplace listing.
                  </Typography>
                  <Typography
                    className={clsx(classes.description, classes.noteWrapper)}
                  >
                    <span className={classes.descriptionNote}>Note:</span> You
                    can edit all information in the next step.
                  </Typography>
                  <SelectV2
                    field={{ ...externalMarketplaceJson }}
                    value={cloud}
                    onChange={handleChange}
                    onBlur={() => handleBlur('cloud')}
                    isError={!!(touched.cloud && errors.cloud)}
                    errorText={
                      touched.cloud && errors.cloud ? errors.cloud : ''
                    }
                  />

                  {cloud && (
                    <TextInputV2
                      keyName="salesforceAppExchangeLink"
                      title="Add Salesforce AppExchange listing link"
                      type="text"
                      value={salesforceAppExchangeLink}
                      onChange={e =>
                        setSalesforceAppExchangeLink(e.target.value)
                      }
                      onBlur={() => handleBlur('salesforceAppExchangeLink')}
                      isRequired
                      errorText={
                        !!(
                          touched.salesforceAppExchangeLink &&
                          errors.salesforceAppExchangeLink
                        ) && errors.salesforceAppExchangeLink
                      }
                      supportingText="Eg. https://appexchange.salesforce.com/appxListingDetail?listingId=xxxxxxxxxxxxxxxxxx"
                      isReadOnly={false}
                      placeHolder={''}
                      isError={
                        !!(
                          touched.salesforceAppExchangeLink &&
                          errors.salesforceAppExchangeLink
                        )
                      }
                    />
                  )}
                </div>
                <div>
                  {errorMessage && (
                    <div className={classes.errorMessage}>{errorMessage}</div>
                  )}
                </div>
              </>
            )}
          </div>
          {listingTypeInfo === FlyoutListingProductType.NEW ? (
            <>
              <div className={classes.productTypeSelection}>
                <Typography className={classes.contentHeading}>
                  Which product category do you intend to list?
                </Typography>
                <div>
                  <RadioButtonsV2
                    checkedValue={{
                      key: awsProductType ?? '',
                      value: awsProductType ?? '',
                    }}
                    name="companyDetails.listingType"
                    radioButtonMappings={AWSProductType}
                    handleChange={handleProductTypeSelection}
                    vertical
                    collapsible
                    divider
                    defaultOpenDescription={false}
                  />
                </div>
              </div>
              {awsProductType ===
                FlyOutAWSProductListingType.PROFESSIONAL_SERVICES ||
              awsProductType === FlyOutAWSProductListingType.CONTAINER ? (
                <>
                  <div className={classes.contactSupportBanner}>
                    <Typography className={classes.contactSupportBannerContent}>
                      Professional service and container products cannot be
                      created through self-serve onboarding. Our team will
                      connect with you to list your product.
                    </Typography>
                    <ButtonV2
                      data-testid="contact-labra-support"
                      styleType="primary"
                      onClick={handleContactClick}
                    >
                      Contact Labra customer support
                    </ButtonV2>
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
      }
      open={openAddProductModal}
      titleAcceptButton={'Confirm'}
      onAccept={
        listingTypeInfo === FlyoutListingProductType.OTHER
          ? onConfirmSFListing
          : onConfirm
      }
      acceptDisabled={disableAccept}
      onDecline={handleModalClose}
      variant={ModalSizeVariants.large}
      disabledAcceptButtonMessage={'Make an appropriate selection.'}
      icon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '20px', height: '16px' }}
        />
      }
      hideCancelIcon={false}
      hideCancelButton={true}
      hideAcceptButton={!showAcceptButton}
      acceptLoading={submitLoading}
      rightHeaderContent={
        <img src={aws} data-testid="aws-logo" className={classes.image} />
      }
    />
  )
}
